import React, { useMemo, useState, useCallback, useRef, useEffect } from 'react';
import type { Moment } from 'moment';
import './index.scss';
import MetricsCardWrap from '@/pages/dashboard/components/MetricsCardWrap';
import classNames from 'classnames';
import editIcon from '@home/assets/svg/studio/home/tickFile.svg';
import { useIntl } from 'umi';
import { Pagination } from 'tea-component';
import TransferModal from '@home/components/TransferModal';
import ReportItem from './components/ReportItem';
import ReportToolbar from './components/ReportToolbar';
import ReportCardList from './components/ReportCardList';
import SelectedReportsTable from './components/SelectedReportsTable';
import EditNotesModal from './components/EditNotesModal';
import {
  IIntelligenceReportsProps,
  IntelligenceReportDataInfo,
  IReportDateRanges,
} from './type';
import type { TTransferOption, ITransferModalRef } from '@home/components/TransferModal/type';
import Loading from '@intelligence/components/Loading';
import { 
  filterAndSortReports,
  filterReportsByTabAndKeyword,
  initializeDefaultSelectedReports,
  sortReportsByCreateTimeDesc,
  getDefaultDisplayTimeRange,
  formatDateRangeToStore,
  hasBulletedListTool,
} from './util';
import { useReportActions } from './hooks/useReportActions';

/**
 * Intelligence Reports 组件
 * 用于在 Studio 首页展示报告列表，支持筛选、排序和编辑
 */
const IntelligenceReports: React.FC<IIntelligenceReportsProps> = (props) => {
  const {
    titleEn,
    titleCn,
    style,
    className,
    tabOption,
    editModalConfig,
    data: propsData,
    modalSize = 1130,
    timeZone = 0,
    changeData,
    pagination,
    category,
    enableEditNotes = false,
    tagSeparatorReg,
    ...restProps
  } = props || {};
  const { locale, formatMessage } = useIntl();
  const isEn = locale === 'en-US';
  
  const {
    titleEn: modalTitleEn,
    titleCn: modalTitleCn,
    leftTitleEn,
    leftTitleCn,
    rightInfoEn,
    rightInfoCn,
    searchPlaceholderEn,
    searchPlaceholderCn,
    defaultSortByCreateTime = false,
    extraToolList,
    haveTopDateFilter = false,
    topDateFilterLabelEn,
    topDateFilterLabelCn,
    summaryExpandable = false,
  } = editModalConfig || {};
  const bulletedListToolConfig = hasBulletedListTool(extraToolList)
    ? extraToolList
    : undefined;
  const { data: reportData, loading } = propsData?.reportList ?? { data: null, loading: false };
  const data = reportData?.resource_list ?? [];
  const [segmentValue, setSegmentValue] = useState(tabOption?.defaultValue);
  const [showEditModal, setShowEditModal] = useState(false);
  const [displaySearchValue, setDisplaySearchValue] = useState(''); // 首页展示区的搜索关键词
  const {has_studio_editor: studioEditor} = propsData?.studioData || {};

  // 前端分页状态
  const hasPagination = !!pagination;
  const defaultPageSize = pagination?.pageSize ?? 5;
  const pageSizeOpts = pagination?.pageSizeOptions ?? [5, 10, 20];
  const [pageIndex, setPageIndex] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(defaultPageSize);

  // Editor's notes Toggle（默认 ON）
  const [editorNotesEnabled, setEditorNotesEnabled] = useState<boolean>(true);

  // 编辑笔记弹窗状态
  const [editingReport, setEditingReport] = useState<IntelligenceReportDataInfo | null>(null);
  const [editNoteLoading, setEditNoteLoading] = useState<boolean>(false);

  /** 维护报告点赞数 - 存储每个报告的最新点赞数 */
  const [reportLikeCounts, setReportLikeCounts] = useState<Record<string, number>>({});
  
  // 使用报告操作 Hook
  const { handleDownloadExcel, handleConfirmSave, handleEditReport } = useReportActions({
    formatMessage,
    studioCode: propsData?.frameCode,
    onDataChange: changeData,
    category,
  });
  
  /**
   * 处理点赞数变化
   * 参考 intelligence 模块的实现，在父组件维护点赞数
   */
  const handleLikeBtnClick = useCallback((resourceId: string, likeCounts?: number) => {
    setReportLikeCounts((prev) => ({
      ...prev,
      [resourceId]: likeCounts ?? (prev[resourceId] ?? 0) + 1,
    }));
  }, []);
  
  /** 筛选和排序要展示的报告列表 */
  const displayReports = useMemo(() => {
    let reports = filterAndSortReports(data, timeZone, segmentValue);
    
    // 如果有搜索关键词，进一步过滤
    if (displaySearchValue?.trim()) {
      const keyword = displaySearchValue.trim().toLowerCase();
      reports = reports.filter((report) => {
        const title = (isEn ? report.title_en : report.title)?.toLowerCase() || '';
        const tags = isEn ? report.tag_en : report.tag;
        const tagMatch = tags?.some(tag => tag?.toLowerCase().includes(keyword)) ?? false;
        return title.includes(keyword) || tagMatch;
      });
    }

    return reports.map((report) => {
      const merged = {
        ...report,
        report_like: reportLikeCounts[report.resource_id] ?? report.report_like,
        // 保留原始值，供 badge 判断是否真正修改过
        _original_introduction: report.introduction,
        _original_introduction_en: report.introduction_en,
        _original_tag: report.tag,
        _original_tag_en: report.tag_en,
      };

      // Toggle ON 时，按当前语言将 studio 字段覆盖到原始字段上
      if (enableEditNotes && editorNotesEnabled) {
        if (isEn) {
          if (report.studio_introduction_en) {
            merged.introduction_en = report.studio_introduction_en;
          }
          if (report.studio_tag_en) {
            merged.tag_en = report.studio_tag_en;
          }
        } else {
          if (report.studio_introduction) {
            merged.introduction = report.studio_introduction;
          }
          if (report.studio_tag) {
            merged.tag = report.studio_tag;
          }
        }
      }

      return merged;
    });
  }, [data, timeZone, segmentValue, displaySearchValue, isEn, reportLikeCounts, editorNotesEnabled]);

  const pagedReports = useMemo(() => {
    if (!hasPagination) return displayReports;
    const start = (pageIndex - 1) * pageSize;
    return displayReports.slice(start, start + pageSize);
  }, [displayReports, hasPagination, pageIndex, pageSize]);

  useEffect(() => {
    if (!hasPagination) return;
    const maxPage = Math.max(1, Math.ceil(displayReports.length / pageSize));
    if (pageIndex > maxPage) {
      setPageIndex(maxPage);
    }
  }, [hasPagination, displayReports.length, pageSize, pageIndex]);
  
  const [activeTabId, setActiveTabId] = useState(tabOption?.defaultValue);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [selectedReports, setSelectedReports] = useState<IntelligenceReportDataInfo[]>([]);
  
  /** 存储每个报告的日期范围 */
  const [reportDateRanges, setReportDateRanges] = useState<IReportDateRanges>({});
  /** 父级展示时间范围 */
  const [parentDateRange, setParentDateRange] = useState<[Moment, Moment]>(
    getDefaultDisplayTimeRange,
  );
  
  /** TransferModal 的 ref */
  const transferModalRef = useRef<ITransferModalRef>(null);
  
  // tab切换
  const tabOptions = useMemo(() => {
    if (!tabOption?.tabOptions) return [];
    return tabOption.tabOptions.map((item) => ({
      value: item.value,
      text: isEn ? item.enName : item.cnName,
    }));
  }, [tabOption?.tabOptions, isEn]);

  /** 编辑弹窗左侧 tabs */
  const leftTabs = useMemo(() => {
    if (!tabOption?.tabOptions || tabOption.tabOptions.length <= 1) return [];
    return tabOption.tabOptions.map((item) => ({
      id: item.value,
      label: isEn ? item.enName : item.cnName,
    }));
  }, [tabOption?.tabOptions, isEn]);

  /** 根据当前 tab 和搜索关键字过滤报告列表（用于编辑弹窗） */
  const filteredReports = useMemo(() => {
    const filtered = filterReportsByTabAndKeyword(data, activeTabId, searchKeyword, isEn);
    // 将维护的点赞数合并到编辑弹窗的报告数据中
    return filtered.map((report) => ({
      ...report,
      report_like: reportLikeCounts[report.resource_id] ?? report.report_like,
    }));
  }, [data, activeTabId, searchKeyword, isEn, reportLikeCounts]);

  // 转换为TransferModal的option格式
  const leftOptions: TTransferOption[] = useMemo(() => {
    return filteredReports.map((item) => ({
      value: item.resource_id,
      text: isEn ? item.title_en : item.title,
      checked: selectedReports.some(selected => selected.resource_id === item.resource_id),
      disabled: false,
      data: item, // 保存完整数据
    }));
  }, [filteredReports, selectedReports, isEn]);

  /** 右侧已选报告列表 */
  const rightOptions: TTransferOption[] = useMemo(() => {
    return selectedReports.map((item) => ({
      value: item.resource_id,
      text: isEn ? item.title_en : item.title,
      checked: true,
      data: item,
    }));
  }, [selectedReports, isEn]);

  /**
   * 自定义左侧报告渲染
   * 使用 ReportItem 组件展示每个报告
   */
  const leftOptionRender = (option: TTransferOption, optionStyle?: React.CSSProperties) => {
    const reportItem = option.data as IntelligenceReportDataInfo;
    if (!reportItem) return null;

    return (
      <ReportItem
        key={option.value}
        data={reportItem}
        checked={option.checked}
        disabled={option.disabled}
        searchKeyword={searchKeyword}
        style={optionStyle}
        tagSeparatorReg={tagSeparatorReg}
        onChange={(checked) => {
          handleReportChange(checked, reportItem);
        }}
      />
    );
  };

  /** 移除报告处理 */
  const handleRemoveReport = useCallback((reportItem: IntelligenceReportDataInfo) => {
    setSelectedReports((prev: IntelligenceReportDataInfo[]) => 
      prev.filter(item => item.resource_id !== reportItem.resource_id)
    );
    setReportDateRanges((prev: IReportDateRanges) => {
      const newRanges = { ...prev };
      delete newRanges[reportItem.resource_id];
      return newRanges;
    });
  }, []);

  /** 子级日期变化处理（不影响父级） */
  const handleDateChange = useCallback((reportItem: IntelligenceReportDataInfo, startTime: string, endTime: string) => {
    setReportDateRanges(prev => ({
      ...prev,
      [reportItem.resource_id]: { startTime, endTime }
    }));
  }, []);

  /** 父级日期变化：批量同步到所有已选子项 */
  const handleParentDateChange = useCallback((range: [Moment, Moment]) => {
    setParentDateRange(range);
    const { startTime, endTime } = formatDateRangeToStore(range);
    setReportDateRanges(prev => {
      const next = { ...prev };
      selectedReports.forEach(report => {
        next[report.resource_id] = { startTime, endTime };
      });
      return next;
    });
  }, [selectedReports]);

  /** 拖拽排序处理 */
  const handleDragEnd = useCallback((newRecords: IntelligenceReportDataInfo[]) => {
    setSelectedReports(newRecords);
  }, []);

  /** 自定义右侧报告渲染 */
  const rightCustomRender = useCallback(() => {
    return (
      <SelectedReportsTable
        selectedReports={selectedReports}
        isEn={isEn}
        showParentDateFilter={haveTopDateFilter}
        topDateFilterLabelEn={topDateFilterLabelEn}
        topDateFilterLabelCn={topDateFilterLabelCn}
        parentDateRange={parentDateRange}
        reportDateRanges={reportDateRanges}
        onParentDateChange={handleParentDateChange}
        onDragEnd={handleDragEnd}
        onRemove={handleRemoveReport}
        onDateChange={handleDateChange}
      />
    );
  }, [
    selectedReports,
    isEn,
    haveTopDateFilter,
    topDateFilterLabelEn,
    topDateFilterLabelCn,
    parentDateRange,
    reportDateRanges,
    handleParentDateChange,
    handleDragEnd,
    handleRemoveReport,
    handleDateChange,
  ]);

  /** 处理报告选择变化 */
  const handleReportChange = (checked: boolean, report: IntelligenceReportDataInfo): void => {
    if (checked) {
      setSelectedReports(prev => {
        const next = [...prev, report];
        return defaultSortByCreateTime
          ? sortReportsByCreateTimeDesc(next)
          : next;
      });
      if (haveTopDateFilter) {
        const { startTime, endTime } = formatDateRangeToStore(parentDateRange);
        setReportDateRanges(prev => ({
          ...prev,
          [report.resource_id]: { startTime, endTime },
        }));
      }
    } else {
      setSelectedReports(prev => prev.filter(item => item.resource_id !== report.resource_id));
    }
  };

  // 处理tab切换
  const handleTabChange = (tabId: string): void => {
    setActiveTabId(tabId);
    // tab 切换时，滚动到顶部
    setTimeout(() => {
      transferModalRef.current?.scrollLeftToTop();
    }, 0);
  };

  // 处理搜索（前端搜索，不需要下拉选项）
  const handleSearch = (value?: string): void => {
    setSearchKeyword(value || '');
    // 搜索时，滚动到顶部
    setTimeout(() => {
      transferModalRef.current?.scrollLeftToTop();
    }, 0);
  };

  // 清空搜索
  const handleClearSearch = (): void => {
    setSearchKeyword('');
  };

  /** 清空所有已选 */
  const handleClearAll = (): void => {
    setSelectedReports([]);
    setReportDateRanges({});
    setParentDateRange(getDefaultDisplayTimeRange());
  };

  /**
   * 初始化默认勾选的报告
   * 打开编辑弹窗时调用
   */
  const initializeSelectedReports = useCallback(() => {
    const { selectedReports: defaultSelected, dateRanges } =
      initializeDefaultSelectedReports(data);
    setSelectedReports(defaultSelected);
    setReportDateRanges(dateRanges);
    if (haveTopDateFilter) {
      setParentDateRange(getDefaultDisplayTimeRange());
    }
  }, [data, haveTopDateFilter]);

  /** 取消或关闭时重置 */
  const handleCancel = (): void => {
    setSelectedReports([]);
    setReportDateRanges({});
    setParentDateRange(getDefaultDisplayTimeRange());
    setSearchKeyword('');
    setActiveTabId(segmentValue ?? tabOption?.defaultValue ?? 'all');
    setShowEditModal(false);
  };

  /** 确认选择 - 调用保存接口（category：当前 Tab 配置优先，否则根 props.category） */
  const handleConfirm = async (): Promise<void> => {
    const activeTabCfg = tabOption?.tabOptions?.find(
      item => item.value === activeTabId,
    );
    const saveCategory = activeTabCfg?.category ?? category;
    const success = await handleConfirmSave(
      selectedReports,
      reportDateRanges,
      saveCategory,
    );
    if (success) {
      setShowEditModal(false);
    }
  };

  // ===== Edit Notes 弹窗逻辑 =====
  const handleOpenEditNotes = useCallback((report: IntelligenceReportDataInfo) => {
    setEditingReport(report);
  }, []);

  const getEditNotesDefaults = useCallback(() => {
    if (!editingReport) return { tags: [] as string[], content: '', originalTags: [] as string[], originalContent: '' };
    if (isEn) {
      return {
        tags: editingReport.studio_tag_en ?? editingReport.tag_en ?? [],
        content: editingReport.studio_introduction_en || editingReport.introduction_en || '',
        originalTags: editingReport.tag_en ?? [],
        originalContent: editingReport.introduction_en || '',
      };
    }
    return {
      tags: editingReport.studio_tag ?? editingReport.tag ?? [],
      content: editingReport.studio_introduction || editingReport.introduction || '',
      originalTags: editingReport.tag ?? [],
      originalContent: editingReport.introduction || '',
    };
  }, [editingReport, isEn]);

  const handleEditNotesConfirm = useCallback(async (tags: string[], content: string) => {
    if (!editingReport) return;
    setEditNoteLoading(true);
    const success = await handleEditReport({
      report: editingReport,
      isEn,
      tags,
      introduction: content,
    });
    setEditNoteLoading(false);
    if (success) {
      setEditingReport(null);
    }
  }, [editingReport, isEn, handleEditReport]);

  const handleEditNotesCancel = useCallback(() => {
    setEditingReport(null);
  }, []);

  const editNotesDefaults = getEditNotesDefaults();

  return (
    <>
      <MetricsCardWrap
        titleCn={titleCn}
        titleEn={titleEn}
        className={classNames("dbh-intelligence-reports", className)}
        style={style}
        operation={
          studioEditor && !loading ? (
            <span
              className={"dbh-intelligence-reports-edit-btn"}
              onClick={() => {
                initializeSelectedReports();
                setActiveTabId(
                  segmentValue ?? tabOption?.defaultValue ?? 'all',
                );
                setShowEditModal(true);
              }}
            >
              <img className='card-sort-charicon' src={editIcon} />
              {isEn ? 'Edit Report' : '编辑报告'}
            </span>
          ) : null
        }
        {...restProps}
      >
        <div className="dbh-intelligence-reports-content">
          <ReportToolbar
            tabOptions={tabOptions}
            segmentValue={segmentValue}
            onSegmentChange={(val) => {
              setSegmentValue(val);
              setPageIndex(1);
            }}
            searchValue={displaySearchValue}
            onSearchChange={(val) => {
              setDisplaySearchValue(val);
              setPageIndex(1);
            }}
            onSearch={(word) => {
              setDisplaySearchValue(word?.trim() || '');
              setPageIndex(1);
            }}
            onClearSearch={() => {
              setDisplaySearchValue('');
              setPageIndex(1);
            }}
            searchPlaceholder={isEn ? 'Look up tags or keywords' : '搜索标签或关键词'}
            showEditorNotesToggle={enableEditNotes}
            editorNotesEnabled={editorNotesEnabled}
            onEditorNotesToggle={setEditorNotesEnabled}
            editorNotesLabel={isEn ? "View Editor's notes" : '查看编辑笔记'}
          />
          {loading ? (
            <Loading classNameValue="dbh-intelligence-reports-loading" />
          ) : (
            <>
              <ReportCardList
                reports={pagedReports}
                isEn={isEn}
                searchKeyword={displaySearchValue}
                studioCode={propsData?.frameCode || ''}
                onDownloadExcel={handleDownloadExcel}
                onLikeChange={handleLikeBtnClick}
                showEditButton={enableEditNotes && !!studioEditor}
                useEditorNotes={enableEditNotes && editorNotesEnabled}
                onEditReport={handleOpenEditNotes}
                extraToolList={bulletedListToolConfig}
                summaryExpandable={summaryExpandable}
                tagSeparatorReg={tagSeparatorReg}
              />
              {hasPagination && displayReports.length > 0 && (
                <div className="dbh-intelligence-reports-pagination">
                  <Pagination
                    recordCount={displayReports.length}
                    pageIndex={pageIndex}
                    pageSize={pageSize}
                    pageSizeOptions={pageSizeOpts}
                    onPagingChange={(query) => {
                      setPageIndex(query.pageIndex || 1);
                      setPageSize(query.pageSize || defaultPageSize);
                    }}
                    stateTextVisible={true}
                    stateText={isEn
                      ? `${displayReports.length} total items`
                      : `共 ${displayReports.length} 条`
                    }
                    pageSizeVisible={displayReports.length > pageSizeOpts[0]}
                    pageIndexVisible={displayReports.length > pageSizeOpts[0]}
                    jumpVisible={displayReports.length > pageSizeOpts[0]}
                    endJumpVisible={displayReports.length > pageSizeOpts[0]}
                  />
                </div>
              )}
            </>
          )}
        </div>
      </MetricsCardWrap>
      {showEditModal && (
        <TransferModal
          ref={transferModalRef}
          visible={showEditModal}
          className={"dbh-intelligence-reports-transfer-modal"}
          isEN={isEn}
          modalSize={modalSize}
          titleEn={modalTitleEn}
          titleCn={modalTitleCn}
          leftTitleEn={leftTitleEn}
          leftTitleCn={leftTitleCn}
          rightInfoEn={rightInfoEn}
          rightInfoCn={rightInfoCn}
          searchPlaceholderEn={searchPlaceholderEn}
          searchPlaceholderCn={searchPlaceholderCn}
          leftLoading={loading}
          rightLoading={loading}
          leftOptions={leftOptions}
          rightOptions={rightOptions}
          leftTabs={leftTabs}
          leftActiveTabId={activeTabId}
          onLeftTabChange={handleTabChange}
          leftOptionRender={leftOptionRender}
          rightCustomRender={rightCustomRender}
          onConfirm={() => {
            handleConfirm().catch((error) => {
              console.error('Confirm failed:', error);
            });
          }}
          onCancel={handleCancel}
          onClearAllOption={handleClearAll}
          onSearch={handleSearch}
          onSearchValueChange={handleSearch}
          onClearSearch={handleClearSearch}
          searchable={true}
          showClearAll={false}
          searchOptions={[]}
          virtual={true}
          dynamicHeight={true}
          rowHeight={100}
          leftListHeight={600}
          rightListHeight={600}
          resetScrollOnOptionsChange={false}
          saveEmptyDisable={false}
        />
      )}
      {!!editingReport && (
        <EditNotesModal
          visible={!!editingReport}
          isEn={isEn}
          loading={editNoteLoading}
          defaultTags={editNotesDefaults.tags}
          defaultContent={editNotesDefaults.content}
          originalTags={editNotesDefaults.originalTags}
          originalContent={editNotesDefaults.originalContent}
          extraToolList={bulletedListToolConfig}
          onConfirm={(tags, content) => {
            handleEditNotesConfirm(tags, content).catch((err) => {
              console.error('Edit notes failed:', err);
            });
          }}
          onCancel={handleEditNotesCancel}
        />
      )}
    </>
  );
};

export default IntelligenceReports;
